/** Windowed pagination for search results. Shows a jump-to-page input when there are many pages. */

import { h } from 'preact';
import { useState } from 'preact/hooks';

interface PaginationProps {
  page: number;
  totalResults: number;
  perPage: number;
  pageResultCount: number;
  onPageChange: (page: number) => void;
}

const JUMP_THRESHOLD = 10;

function getPageItems(current: number, total: number): Array<number | string> {
  if (total <= 7) {
    return Array.from({ length: total }, (_, index) => index + 1);
  }

  const pages: Array<number | string> = [1];
  const windowStart = Math.max(2, current - 1);
  const windowEnd = Math.min(total - 1, current + 1);

  if (windowStart > 2) pages.push('ellipsis-start');
  for (let page = windowStart; page <= windowEnd; page += 1) {
    pages.push(page);
  }
  if (windowEnd < total - 1) pages.push('ellipsis-end');
  pages.push(total);

  return pages;
}

export function Pagination({ page, totalResults, perPage, pageResultCount, onPageChange }: PaginationProps) {
  const [jumpValue, setJumpValue] = useState('');
  if (totalResults === 0) return null;

  const start = pageResultCount > 0 ? (page - 1) * perPage + 1 : 0;
  const end = pageResultCount > 0 ? Math.min(start + pageResultCount - 1, totalResults) : 0;
  const totalPages = Math.max(1, Math.ceil(totalResults / perPage));
  const hasPrevious = page > 1;
  const hasNext = page < totalPages && pageResultCount > 0;
  const pageItems = getPageItems(page, totalPages);

  const handleJump = (e: Event) => {
    e.preventDefault();
    const n = parseInt(jumpValue, 10);
    if (!Number.isFinite(n) || n < 1 || n > totalPages || n === page) return;
    onPageChange(n);
    setJumpValue('');
  };

  return (
    <div class="ts-pagination">
      <p class="ts-pagination-info">
        Showing <strong>{start.toLocaleString()}&ndash;{end.toLocaleString()}</strong> of {totalResults.toLocaleString()} results
        {totalPages > 1 && <span class="ts-pagination-page-of"> · Page {page} of {totalPages.toLocaleString()}</span>}
      </p>

      {totalPages > 1 && (
        <div class="ts-pagination-buttons">
          <button
            class="ts-pagination-btn ts-pagination-btn--nav"
            disabled={!hasPrevious}
            onClick={() => onPageChange(page - 1)}
            aria-label="Previous page"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="14" height="14">
              <polyline points="15,18 9,12 15,6" />
            </svg>
            <span class="ts-pagination-btn-label">Prev</span>
          </button>

          <div class="ts-pagination-pages" aria-label={`Page ${page} of ${totalPages}`}>
            {pageItems.map((item, index) => item === 'ellipsis-start' || item === 'ellipsis-end' ? (
              <span key={`${item}-${index}`} class="ts-pagination-ellipsis" aria-hidden="true">
                &hellip;
              </span>
            ) : (
              <button
                key={item}
                class={`ts-pagination-btn ts-pagination-btn--page ${item === page ? 'ts-pagination-btn--active' : ''}`}
                onClick={() => onPageChange(item as number)}
                aria-current={item === page ? 'page' : undefined}
                aria-label={`Go to page ${item}`}
              >
                {item}
              </button>
            ))}
          </div>

          <button
            class="ts-pagination-btn ts-pagination-btn--nav"
            disabled={!hasNext}
            onClick={() => onPageChange(page + 1)}
            aria-label="Next page"
          >
            <span class="ts-pagination-btn-label">Next</span>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="14" height="14">
              <polyline points="9,18 15,12 9,6" />
            </svg>
          </button>

          {totalPages > JUMP_THRESHOLD && (
            <form class="ts-pagination-jump" onSubmit={handleJump}>
              <label class="ts-pagination-jump-label" for="ts-pagination-jump-input">Go to</label>
              <input
                id="ts-pagination-jump-input"
                class="ts-pagination-jump-input"
                type="number"
                min={1}
                max={totalPages}
                inputMode="numeric"
                value={jumpValue}
                placeholder={String(page)}
                onInput={(e) => setJumpValue((e.target as HTMLInputElement).value)}
                aria-label={`Jump to page (1 to ${totalPages})`}
              />
              <button type="submit" class="ts-pagination-jump-go" disabled={!jumpValue}>Go</button>
            </form>
          )}
        </div>
      )}
    </div>
  );
}
